import { AxiosResponse } from "axios";
import { createServerApiClient } from "../server-base-client";

export type PaymentIntentResponse = {
  clientSecret: string;
  paymentIntentId: string;
  total: number;
  subTotal: number;
  product: {
    id: string;
    variant: string;
    price: number;
  };
  appliedAddons: Array<{
    name: string;
    price: number;
    id: string;
    key: string;
  }>;
  addons: Array<{
    name: string;
    price: number;
    id: string;
    key: string;
  }>;
  discount?: {
    code: string;
    type: "fixed" | "percentage";
    discount: number;
    freeAddon?: string;
  };
  taxAmount?: number;
};

export type PaymentStatusResponse = {
  paymentIntentId: string;
  total: number;
  subTotal: number;
  product: {
    id: string;
    variant: string;
    price: number;
  };
  appliedAddons?: Array<{
    id: string;
    name: string;
    price: number;
    key: string;
  }>;
  discount?: {
    code: string;
    type: "fixed" | "percentage";
    discount: number;
    freeAddon?: string;
  };
  status?: string;
  email?: string;
  taxAmount?: number;
};

export type PaymentChargeDto = {
  id: string;
  total: number;
  subTotal: number;
  billingDetails: {
    email: string;
    name: string;
    phone: string;
    address: {
      line1: string;
      line2: string;
    };
  };
};

type CreatePaymentIntentParams = {
  yzAnonymousId: string;
  product: string;
  email?: string;
  phone?: string;
  address?: string;
  addons?: string[];
  promotionalCode?: string;
};

type UpdatePaymentIntentParams = {
  product: string;
  addons?: string[];
  promotionalCode?: string;
  email?: string;
  phone?: string;
  userId?: string;
  aliasAccount?: boolean;
  zipCode?: string;
  fullName?: string;
  address?: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
};

export type FindUserProjectByEmailResponse = {
  userId: string;
  projects: string[];
};

const getApiClient = async (authToken?: string) => {
  const client = await createServerApiClient(authToken);

  // Add API key header to the client's default headers
  client.defaults.headers.common["x-api-key"] = process.env.PAYMENTS_V2_API_KEY;

  return client;
};

export async function createPaymentIntent(
  params: CreatePaymentIntentParams,
  idempotencyKey?: string,
  authToken?: string,
): Promise<AxiosResponse<PaymentIntentResponse>> {
  const client = await getApiClient(authToken);

  const headers = idempotencyKey
    ? {
        "Idempotency-Key": idempotencyKey,
      }
    : undefined;

  return await client.post(
    "payments-v2",
    {
      ...params,
    },
    { headers },
  );
}

export async function updatePaymentIntent(
  paymentIntentId: string,
  params: UpdatePaymentIntentParams,
  authToken?: string,
): Promise<AxiosResponse<PaymentIntentResponse>> {
  const client = await getApiClient(authToken);

  return await client.patch(`payments-v2/${paymentIntentId}`, {
    ...params,
  });
}

export async function getPaymentIntentStatus(
  paymentIntentId: string,
  authToken?: string,
): Promise<AxiosResponse<PaymentStatusResponse>> {
  const client = await getApiClient(authToken);

  try {
    return await client.get(`payments-v2/${paymentIntentId}/status`);
  } catch (error) {
    console.error("Error getting payment intent status:", error);
    throw error;
  }
}

export async function getLatestCharge(
  paymentIntentId: string,
  authToken?: string,
): Promise<AxiosResponse<PaymentChargeDto>> {
  const client = await getApiClient(authToken);
  return await client.get(`payments-v2/${paymentIntentId}/latest-charge`);
}

export async function findUserByEmail(
  email: string,
  authToken?: string,
): Promise<AxiosResponse<FindUserProjectByEmailResponse>> {
  const client = await getApiClient(authToken);

  return await client.get(`auth-util/find-by-email`, {
    params: { email },
  });
}
